import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { planoService } from '../../services/planoService';
import { PlanoInput, AcessoChoiceLabels, AcessoChoiceNumericToStringKey } from '../../types/plano';

interface PlanoFormProps {
  initialData?: PlanoInput;
  isEditing?: boolean;
}

export const PlanoForm: React.FC<PlanoFormProps> = ({ initialData, isEditing }) => {
  const { register, handleSubmit, control, formState: { errors, isSubmitting }, reset } = useForm<PlanoInput>({
    defaultValues: {
      nome: '',
      descricao: '',
      valor: '',
      max_membros: 1,
      quantidade_cidade: 1,
      quantidade_segmento: 1,
      acessos: [],
      limite_cidades_extra: 0,
      limite_segmentos_extra: 0,
      valor_cidade_adicional: '',
      valor_segmento_adicional: '',
    }
  });

  useEffect(() => {
    if (initialData) {
      const processedData = { ...initialData };
      if (initialData.acessos) {
        processedData.acessos = initialData.acessos.map(acesso => {
          const numericAcesso = typeof acesso === 'string' ? parseInt(acesso, 10) : acesso;
          if (typeof numericAcesso === 'number' && AcessoChoiceNumericToStringKey[numericAcesso]) {
            return AcessoChoiceNumericToStringKey[numericAcesso];
          }
          return acesso; // Return original value if it's not a number or not in the map
        });
      }
      reset(processedData);
    }
  }, [initialData, reset]);

  const navigate = useNavigate();
  const { id } = useParams();

  const onSubmit = async (data: PlanoInput) => {
    try {
      const payload = {
        ...data,
        max_membros: Number(data.max_membros),
        quantidade_cidade: Number(data.quantidade_cidade),
        quantidade_segmento: Number(data.quantidade_segmento),
        limite_cidades_extra: Number(data.limite_cidades_extra),
        limite_segmentos_extra: Number(data.limite_segmentos_extra),
      };

      if (isEditing && id) {
        await planoService.atualizar(Number(id), payload);
        toast.success('Plano atualizado com sucesso!');
      } else {
        await planoService.criar(payload);
        toast.success('Plano criado com sucesso!');
      }
      navigate('/planos');
    } catch (error) {
      toast.error('Erro ao salvar plano. Por favor, tente novamente.');
      console.error('Erro ao salvar plano:', error);
    }
  };

  const formatarValor = (event: React.ChangeEvent<HTMLInputElement>) => {
    let valor = event.target.value.replace(/\D/g, '');
    valor = (parseFloat(valor) / 100).toFixed(2);
    event.target.value = valor;
  };

  return (
    <div className="max-w-2xl mx-auto p-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditing ? 'Editar Plano' : 'Novo Plano'}
        </h1>
        <p className="mt-2 text-sm text-gray-700">
          {isEditing ? 'Atualize os dados do plano' : 'Preencha os dados para criar um novo plano'}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <label htmlFor="nome" className="block text-sm font-medium text-gray-700">
            Nome
          </label>
          <input
            type="text"
            id="nome"
            {...register('nome', { 
              required: 'Nome é obrigatório',
              maxLength: { value: 255, message: 'Nome deve ter no máximo 255 caracteres' }
            })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          />
          {errors.nome && (
            <p className="mt-1 text-sm text-red-600">{errors.nome.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="descricao" className="block text-sm font-medium text-gray-700">
            Descrição
          </label>
          <textarea
            id="descricao"
            rows={3}
            {...register('descricao', { required: 'Descrição é obrigatória' })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          />
          {errors.descricao && (
            <p className="mt-1 text-sm text-red-600">{errors.descricao.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="valor" className="block text-sm font-medium text-gray-700">
            Valor (R$)
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-500 sm:text-sm">R$</span>
            </div>
            <input
              type="text"
              id="valor"
              {...register('valor', { 
                required: 'Valor é obrigatório',
                pattern: {
                  value: /^-?\d{0,6}(?:\.\d{0,2})?$/,
                  message: 'Valor deve ser um número válido (ex: 99.99)'
                }
              })}
              onChange={formatarValor}
              placeholder="0,00"
              className="pl-12 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
          {errors.valor && (
            <p className="mt-1 text-sm text-red-600">{errors.valor.message}</p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label htmlFor="max_membros" className="block text-sm font-medium text-gray-700">
              Máx. Membros
            </label>
            <input
              type="number"
              id="max_membros"
              {...register('max_membros', { required: 'Campo obrigatório', valueAsNumber: true, min: { value: 1, message: 'Mínimo de 1 membro' } })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
            {errors.max_membros && (
              <p className="mt-1 text-sm text-red-600">{errors.max_membros.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="quantidade_cidade" className="block text-sm font-medium text-gray-700">
              Qtd. Cidades
            </label>
            <input
              type="number"
              id="quantidade_cidade"
              {...register('quantidade_cidade', { required: 'Campo obrigatório', valueAsNumber: true, min: { value: 1, message: 'Mínimo de 1 cidade' } })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
            {errors.quantidade_cidade && (
              <p className="mt-1 text-sm text-red-600">{errors.quantidade_cidade.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="quantidade_segmento" className="block text-sm font-medium text-gray-700">
              Qtd. Segmentos
            </label>
            <input
              type="number"
              id="quantidade_segmento"
              {...register('quantidade_segmento', { required: 'Campo obrigatório', valueAsNumber: true, min: { value: 1, message: 'Mínimo de 1 segmento' } })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
            {errors.quantidade_segmento && (
              <p className="mt-1 text-sm text-red-600">{errors.quantidade_segmento.message}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="limite_cidades_extra" className="block text-sm font-medium text-gray-700">
              Limite Cidades Extra
            </label>
            <input
              type="number"
              id="limite_cidades_extra"
              {...register('limite_cidades_extra', { required: 'Campo obrigatório', valueAsNumber: true, min: { value: 0, message: 'Mínimo de 0' } })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
            {errors.limite_cidades_extra && (
              <p className="mt-1 text-sm text-red-600">{errors.limite_cidades_extra.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="limite_segmentos_extra" className="block text-sm font-medium text-gray-700">
              Limite Segmentos Extra
            </label>
            <input
              type="number"
              id="limite_segmentos_extra"
              {...register('limite_segmentos_extra', { required: 'Campo obrigatório', valueAsNumber: true, min: { value: 0, message: 'Mínimo de 0' } })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
            {errors.limite_segmentos_extra && (
              <p className="mt-1 text-sm text-red-600">{errors.limite_segmentos_extra.message}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="valor_cidade_adicional" className="block text-sm font-medium text-gray-700">
              Valor Cidade Adicional (R$)
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm">R$</span>
              </div>
              <input
                type="text"
                id="valor_cidade_adicional"
                {...register('valor_cidade_adicional', {
                  pattern: {
                    value: /^-?\d{0,6}(?:\.\d{0,2})?$/,
                    message: 'Valor deve ser um número válido (ex: 99.99)'
                  }
                })}
                onChange={formatarValor}
                placeholder="0,00"
                className="pl-12 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
            {errors.valor_cidade_adicional && (
              <p className="mt-1 text-sm text-red-600">{errors.valor_cidade_adicional.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="valor_segmento_adicional" className="block text-sm font-medium text-gray-700">
              Valor Segmento Adicional (R$)
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm">R$</span>
              </div>
              <input
                type="text"
                id="valor_segmento_adicional"
                {...register('valor_segmento_adicional', {
                  pattern: {
                    value: /^-?\d{0,6}(?:\.\d{0,2})?$/,
                    message: 'Valor deve ser um número válido (ex: 99.99)'
                  }
                })}
                onChange={formatarValor}
                placeholder="0,00"
                className="pl-12 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
            {errors.valor_segmento_adicional && (
              <p className="mt-1 text-sm text-red-600">{errors.valor_segmento_adicional.message}</p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Acessos Permitidos
          </label>
          <Controller
            name="acessos"
            control={control}
            render={({ field }) => (
              <div className="mt-2 grid grid-cols-2 gap-4">
                {Object.entries(AcessoChoiceLabels).map(([value, label]) => (
                  <label key={value} className="flex items-center">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      checked={field.value.includes(value)}
                      onChange={e => {
                        const newAcessos = e.target.checked
                          ? [...field.value, value]
                          : field.value.filter(val => val !== value);
                        field.onChange(newAcessos);
                      }}
                    />
                    <span className="ml-3 text-sm text-gray-600">{label}</span>
                  </label>
                ))}
              </div>
            )}
          />
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/planos')}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {isSubmitting ? 'Salvando...' : isEditing ? 'Atualizar' : 'Criar'}
          </button>
        </div>
      </form>
    </div>
  );
};
 